import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/siteData';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sugargoospread.us';
const siteUrlTrailing = siteUrl.replace(/\/$/, '');

export default function ArticlePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleUrl = `${siteUrlTrailing}/article/sugargoo-spreadsheet-guide`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrlTrailing}/` },
      { '@type': 'ListItem', position: 2, name: 'Sugargoo Spreadsheet Guide', item: articleUrl },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'The Complete Sugargoo Spreadsheet Guide: Find Products, Save Money & Shop Smarter in 2026',
    description: 'Comprehensive guide covering everything about Sugargoo Spreadsheet — product discovery, price comparison, shipping strategies, QC photos, seller ratings and safety tips for beginners and experienced shoppers.',
    datePublished: '2026-08-01',
    dateModified: '2026-08-04',
    author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Hub' },
    publisher: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Hub', url: `${siteUrlTrailing}/` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'What exactly is a Sugargoo Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'A Sugargoo Spreadsheet is a curated document that organizes product listings found through Sugargoo, including prices, seller information, QC photo references and community ratings.' } },
      { '@type': 'Question', name: 'Is using a Sugargoo Spreadsheet safe?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, using a trusted Sugargoo Spreadsheet is safe as long as you source it from a reliable platform and complete all transactions through Sugargoo with buyer protection enabled.' } },
      { '@type': 'Question', name: 'How often are the spreadsheets updated?', acceptedAnswer: { '@type': 'Answer', text: 'Our Sugargoo Spreadsheet resources are updated frequently — typically several times per week — to reflect new product discoveries, price changes and community feedback.' } },
      { '@type': 'Question', name: 'Do I need a Sugargoo account to use these resources?', acceptedAnswer: { '@type': 'Answer', text: 'While you can browse spreadsheet resources without an account, you will need a Sugargoo account to actually purchase items. Creating an account is free and straightforward.' } },
      { '@type': 'Question', name: 'Are there spreadsheets for specific product types?', acceptedAnswer: { '@type': 'Answer', text: 'Absolutely. Our resources are organized across 14 categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes and Other Stuff.' } },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Simplified Navbar for article pages */}
      <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group cursor-pointer">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-file-list-3-line text-lg"></i>
              </div>
              <span className="font-heading text-lg font-semibold text-foreground-900 tracking-tight whitespace-nowrap">
                Sugargoo Spreadsheet
              </span>
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Breadcrumb */}
        <div className="max-w-[860px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">Sugargoo Spreadsheet Guide</span>
          </nav>
        </div>

        {/* Article Content */}
        <article className="max-w-[860px] mx-auto px-4 md:px-6 pb-20">
          {/* Article Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold whitespace-nowrap">Comprehensive Guide</span>
              <span className="text-xs text-foreground-400">12 min read</span>
              <span className="text-xs text-foreground-400">Updated August 2026</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              The Complete Sugargoo Spreadsheet Guide: Find Products, Save Money &amp; Shop Smarter in 2026
            </h1>
            <p className="text-base text-foreground-500 leading-relaxed">
              Whether you are a complete beginner or an experienced shopper looking to level up, this comprehensive Sugargoo Spreadsheet guide covers everything — from product discovery and price comparison to shipping strategies and safety tips.
            </p>
          </header>

          {/* Featured Image */}
          <div className="w-full h-[380px] md:h-[440px] rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20clean%20desk%20workspace%20with%20an%20ultrawide%20monitor%20displaying%20colorful%20organized%20spreadsheet%20interface%20with%20product%20photos%20and%20category%20filters%2C%20warm%20cream%20and%20beige%20tones%20throughout%2C%20soft%20natural%20lighting%20from%20a%20large%20architectural%20window%2C%20a%20notebook%20with%20handwritten%20notes%20and%20a%20ceramic%20coffee%20cup%20on%20the%20desk%2C%20clean%20editorial%20productivity%20photography%20with%20gentle%20shadows%20and%20airy%20professional%20atmosphere&width=1600&height=900&seq=article-hero-v2&orientation=landscape"
              alt="Complete Sugargoo Spreadsheet shopping guide with organized product listings on a modern large monitor workspace"
              title="Sugargoo Spreadsheet Complete Shopping Guide 2026"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Table of Contents */}
          <div className="bg-background-100 rounded-xl border border-background-200/60 p-6 mb-10">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Table of Contents</h2>
            <nav className="flex flex-col gap-2">
              {[
                'What Is a Sugargoo Spreadsheet and Why You Need One',
                'How to Access and Navigate Sugargoo Spreadsheets',
                'Product Categories: From Shoes to Accessories',
                'Understanding Quality Control Photos and Seller Ratings',
                'Price Comparison: Getting the Best Deals',
                'Shipping Methods and Cost Breakdown',
                'Safety Tips for Shopping via Sugargoo Spreadsheet',
                'Common Mistakes Beginners Should Avoid',
                'Frequently Asked Questions',
              ].map((item, idx) => (
                <a
                  key={idx}
                  href={`#section-${idx + 1}`}
                  className="text-sm text-foreground-600 hover:text-primary-700 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center rounded bg-primary-100 text-primary-700 text-xs font-semibold flex-shrink-0">{idx + 1}</span>
                  {item}
                </a>
              ))}
            </nav>
          </div>

          {/* Section 1 */}
          <section id="section-1" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              What Is a Sugargoo Spreadsheet and Why You Need One
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              A <strong>Sugargoo Spreadsheet</strong> is a structured, organized document that compiles product listings, seller information, quality control references and pricing data into one accessible format. Instead of spending hours browsing individual product pages and comparing options manually, a well-maintained spreadsheet gives you a bird&apos;s-eye view of what is available, what is well-reviewed and what represents good value.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              The concept emerged from the Sugargoo shopping community, where experienced users began sharing their curated finds in spreadsheet format. Over time, these resources have evolved into comprehensive databases covering everything from sneakers and streetwear to accessories and home goods. The beauty of a Sugargoo Spreadsheet lies in its simplicity: rows and columns of data that cut through the noise and help you make informed purchasing decisions quickly.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              Why do thousands of shoppers rely on these spreadsheets? The answer is efficiency. When you are browsing through Sugargoo, the sheer volume of available products can be overwhelming. A spreadsheet filters out low-quality listings, highlights items with strong seller ratings and organizes products by category — essentially doing the heavy lifting so you can focus on finding what you actually want. It is particularly valuable for newcomers who might not yet know which sellers are reliable or what price ranges are reasonable for different product types.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Our Sugargoo Spreadsheet resources at <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Sugargoo Spreadsheet Hub</Link> are updated regularly and organized across 14 product categories, making it easier than ever to discover products that match your style and budget.
            </p>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              How to Access and Navigate Sugargoo Spreadsheets
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              Getting started with a Sugargoo Spreadsheet is straightforward. Most spreadsheets are shared as online documents that you can view directly in your browser — no special software required. The key is knowing where to find the most up-to-date and comprehensive versions, which is exactly why we built this resource hub.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              When you first open a spreadsheet, you will typically see multiple tabs or sheets organized by category. Each row represents an individual product, with columns showing details such as the product name, price in yuan, seller name, quality control photo references and community ratings. The best spreadsheets include direct links to product pages, allowing you to jump from the spreadsheet straight to the item you are interested in.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed">
              To access our complete collection, visit the <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">main Sugargoo Spreadsheet website</a> where you will find all categories organized and ready to browse. You can also use the category dropdown on our <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">homepage</Link> to jump directly to specific product types.
            </p>
          </section>

          {/* Section 3 - Categories */}
          <section id="section-3" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Product Categories: From Shoes to Accessories
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              One of the biggest advantages of using a Sugargoo Spreadsheet is the breadth of categories available. Our resources span 14 distinct product categories, ensuring that whatever you are looking for — sneakers, hoodies, accessories or something more niche — there is a dedicated section for it.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-3 bg-background-100 rounded-lg border border-background-200/60 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-all cursor-pointer text-center whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Each category page on our main site features curated product selections, price ranges and direct links so you can start browsing immediately without any guesswork.
            </p>
          </section>

          {/* Section 4 - QC */}
          <section id="section-4" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Understanding Quality Control Photos and Seller Ratings
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              Quality Control photos, commonly referred to as QC photos, are one of the most valuable features of the Sugargoo shopping experience — and a good Sugargoo Spreadsheet will help you interpret them correctly. When you order a product, the Sugargoo warehouse team photographs the actual item you will receive before shipping it internationally. These photos allow you to verify that the product matches the listing description and meets your quality expectations.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              A well-organized spreadsheet includes references to QC photo standards for different product types. For example, when buying sneakers, you will want to check stitching quality, logo placement accuracy, material consistency and overall shape. For clothing, key QC points include fabric quality, tag accuracy and sizing measurements. Learning to read these photos effectively can save you from receiving items that do not match your expectations.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Seller ratings within the spreadsheet provide another layer of confidence. Ratings are typically based on community feedback covering factors such as communication responsiveness, shipping speed and product accuracy. Prioritizing sellers with consistently high ratings is one of the simplest ways to improve your shopping success rate.
            </p>
          </section>

          {/* Section 5 - Price */}
          <section id="section-5" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Price Comparison: Getting the Best Deals
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              Price comparison is where a Sugargoo Spreadsheet truly shines. Instead of opening dozens of browser tabs and manually noting prices, a spreadsheet presents price data side by side in a clean, comparable format. This makes it significantly easier to spot overpriced listings and identify genuine bargains.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              When using price data from a spreadsheet, keep a few things in mind. First, prices are typically listed in Chinese Yuan, so having a rough conversion rate in mind helps contextualize costs. Second, the cheapest option is not always the best — consider seller ratings and QC photo history alongside price. Third, some spreadsheets include notes about whether a particular price is considered a good deal within the community, which can be invaluable for newcomers.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Our spreadsheet resources on the <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">main Sugargoo platform</a> include community-vetted pricing benchmarks so you always know whether you are getting fair value.
            </p>
          </section>

          {/* Section 6 - Shipping */}
          <section id="section-6" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Shipping Methods and Cost Breakdown
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              Shipping is often the most confusing part of the Sugargoo experience for beginners, and understanding your options can make a significant difference in total cost. Sugargoo offers multiple shipping lines, each with different price points, delivery speeds and package size limitations. A comprehensive Sugargoo Spreadsheet will typically include shipping cost estimates or calculators to help you plan.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              The main shipping methods include economy lines, which are the most affordable but slowest; standard lines offering a balance of speed and cost; and express lines for those who need their items quickly. Additionally, Sugargoo provides a warehouse consolidation service: you can store multiple items in their warehouse and ship them together in one package, which often reduces per-item shipping costs significantly.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed">
              For detailed shipping cost breakdowns by country and weight, visit our <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">main website</a> where we provide up-to-date shipping information along with all product categories.
            </p>
          </section>

          {/* Section 7 - Safety */}
          <section id="section-7" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Safety Tips for Shopping via Sugargoo Spreadsheet
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              Safety should always be a priority when shopping through any platform, and Sugargoo Spreadsheet shopping is no exception. Here are essential safety practices every shopper should follow:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm text-foreground-600 leading-relaxed mb-3">
              <li><strong>Verify spreadsheet sources:</strong> Only use spreadsheets from trusted, well-known community members or established platforms. Be wary of newly created accounts sharing spreadsheets with suspiciously low prices.</li>
              <li><strong>Check seller history:</strong> Before purchasing, look at the seller&apos;s rating history within Sugargoo. Established sellers with consistent positive feedback are always safer bets.</li>
              <li><strong>Review QC photos carefully:</strong> Do not skip the QC photo step. Request additional photos if the initial set is unclear, and compare them against the listing images.</li>
              <li><strong>Use Sugargoo&apos;s buyer protection:</strong> Always complete transactions through Sugargoo rather than arranging payments outside the platform. The built-in buyer protection is your safety net.</li>
              <li><strong>Keep records:</strong> Save screenshots of product listings, chat conversations with sellers and QC photos until you have received and confirmed your items.</li>
            </ul>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Remember that our <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Sugargoo Spreadsheet Hub</Link> is an independent resource — we are not the official Sugargoo website. Always verify information directly on the Sugargoo platform before making purchase decisions.
            </p>
          </section>

          {/* Section 8 - Mistakes */}
          <section id="section-8" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Common Mistakes Beginners Should Avoid
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-3">
              Learning from others&apos; mistakes is the fastest way to improve your Sugargoo Spreadsheet shopping experience. Here are the most common pitfalls and how to avoid them:
            </p>
            <div className="space-y-4">
              {[
                { mistake: 'Ignoring shipping weight estimates', fix: 'Always factor shipping costs into your total budget. A cheap item with expensive shipping may cost more overall than a slightly pricier item from a more efficient shipping route.' },
                { mistake: 'Buying from unverified sellers', fix: 'Stick to sellers with established ratings and multiple positive reviews. The spreadsheet makes this easy by organizing seller data — use it.' },
                { mistake: 'Skipping QC photos', fix: 'Even if a seller has great reviews, always review QC photos. Production batches can vary, and what looked great last month might not be consistent this month.' },
                { mistake: 'Not understanding size charts', fix: 'Asian sizing often differs significantly from Western sizing. Always check the provided measurements against your own measurements rather than relying on letter sizes.' },
                { mistake: 'Rushing through the process', fix: 'Take your time. The spreadsheet is designed to make shopping faster, but that does not mean you should skip steps. A few extra minutes of research can prevent disappointment.' },
              ].map((item, idx) => (
                <div key={idx} className="p-4 bg-background-100 rounded-lg border border-background-200/60">
                  <p className="text-sm font-semibold text-foreground-800 mb-1">
                    <span className="text-accent-600">Mistake:</span> {item.mistake}
                  </p>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    <span className="text-primary-600 font-medium">Fix:</span> {item.fix}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 9 - FAQ */}
          <section id="section-9" className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Frequently Asked Questions About Sugargoo Spreadsheet
            </h2>
            <div className="space-y-4">
              {[
                { q: 'What exactly is a Sugargoo Spreadsheet?', a: 'A Sugargoo Spreadsheet is a curated document that organizes product listings found through Sugargoo, including prices, seller information, QC photo references and community ratings. It helps shoppers discover products faster and make more informed purchasing decisions without manually searching through thousands of individual listings.' },
                { q: 'Is using a Sugargoo Spreadsheet safe?', a: 'Yes, using a trusted Sugargoo Spreadsheet is safe as long as you source it from a reliable platform and complete all transactions through Sugargoo with buyer protection enabled. Our resources are independently maintained and regularly updated to include only verified information.' },
                { q: 'How often are the spreadsheets updated?', a: 'Our Sugargoo Spreadsheet resources are updated frequently — typically several times per week — to reflect new product discoveries, price changes and community feedback. We recommend checking back regularly for the latest additions.' },
                { q: 'Do I need a Sugargoo account to use these resources?', a: 'While you can browse spreadsheet resources without an account, you will need a Sugargoo account to actually purchase items. Creating an account is free and straightforward.' },
                { q: 'Are there spreadsheets for specific product types?', a: 'Absolutely. Our resources are organized across 14 categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes and Other Stuff — each with its own dedicated section.' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
                  <summary className="px-5 py-4 text-sm font-semibold text-foreground-800 cursor-pointer flex items-center justify-between hover:text-foreground-900 transition-colors">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Banner */}
          <div className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
              Ready to Start Shopping?
            </h2>
            <p className="text-primary-100 text-sm max-w-lg mx-auto mb-6 leading-relaxed">
              Access our complete Sugargoo Spreadsheet with all 14 product categories, verified seller information and up-to-date pricing. Your next great find is just a click away.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-8 py-3.5 bg-background-50 text-primary-700 font-semibold rounded-lg hover:bg-background-100 transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Explore Sugargoo Spreadsheet
              </a>
              <Link
                to="/"
                className="px-8 py-3.5 bg-primary-600 text-background-50 font-semibold rounded-lg hover:bg-primary-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          {/* Related Links */}
          <div className="border-t border-background-200/60 pt-8">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">Related Resources</h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="px-4 py-2 bg-background-100 rounded-lg border border-background-200/60 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-all cursor-pointer">
                Sugargoo Spreadsheet Home
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-4 py-2 bg-background-100 rounded-lg border border-background-200/60 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-all">
                Main Website
              </a>
              <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="px-4 py-2 bg-background-100 rounded-lg border border-background-200/60 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-all">
                Shoes Category
              </a>
              <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="px-4 py-2 bg-background-100 rounded-lg border border-background-200/60 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-all">
                Hoodies / Sweaters
              </a>
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </main>

      {/* Footer */}
      <footer className="bg-background-800 text-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Link to="/" className="flex items-center gap-2 cursor-pointer">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className="ri-file-list-3-line text-sm"></i>
                </div>
                <span className="font-heading text-sm font-semibold text-background-50 whitespace-nowrap">Sugargoo Spreadsheet</span>
              </Link>
            </div>
            <p className="text-xs text-background-500">
              &copy; {new Date().getFullYear()} Sugargoo Spreadsheet Hub. Independent resource. <Link to="/" className="hover:text-background-300 transition-colors cursor-pointer">Home</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}